import { Image as I, ImageProps as IP, ImageSource } from "expo-image";
import {
  fetchQuery,
  graphql,
  useFragment,
  useRelayEnvironment,
} from "react-relay";
import { ImageRefetchQuery } from "./__generated__/ImageRefetchQuery.graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSubscriptionWithSkip } from "widgets/src/utils/useSubscriptionWithSkip";
import { ImageFinishUploadSubscription } from "./__generated__/ImageFinishUploadSubscription.graphql";
import { PixelRatio, Platform } from "react-native";
import { Image_image$key } from "./__generated__/Image_image.graphql";

export type RemoteImageSource = {
  image: Image_image$key & { id: string };

  uploadedImages?: Record<string, { localImageURI: string }>;
};
export type RemoteImageProps = Omit<IP, "source"> & RemoteImageSource;

export function RemoteImage(_props: RemoteImageProps) {
  const { uploadedImages, ...props } = _props;
  const image = useFragment(
    graphql`
      fragment Image_image on Image {
        id
        thumbhash
        sizes {
          id
          url
          urlExpiresAt
          height
          width
        }
      }
    `,
    props.image
  );

  useSubscriptionWithSkip<ImageFinishUploadSubscription>(
    {
      variables: {
        id: image?.id,
      },
      subscription: graphql`
        subscription ImageFinishUploadSubscription($id: ID!) {
          imageFinishedUploading(imageIds: [$id]) {
            id
            ...Image_image
          }
        }
      `,
    },
    {
      skip: !image?.id || (image?.sizes?.length ?? 0) > 0,
    }
  );

  const relay = useRelayEnvironment();
  const [isRefetching, setIsRefetching] = useState(false);

  const refetchCount = useRef(0);

  const imageId = image?.id;

  useEffect(() => {
    refetchCount.current = 0;
  }, [imageId]);

  const refetchQuery = useCallback(async () => {
    if (isRefetching || refetchCount.current > 10) {
      return;
    }

    await new Promise((resolve) =>
      setTimeout(resolve, 500 * 2 ** refetchCount.current)
    );
    refetchCount.current++;
    setIsRefetching(true);
    fetchQuery<ImageRefetchQuery>(
      relay,
      graphql`
        query ImageRefetchQuery($id: ID!) {
          image(id: $id) {
            id
            ...Image_image
          }
        }
      `,
      {
        id: imageId,
      },
      {
        fetchPolicy: "network-only",
      }
    )
      .toPromise()
      .finally(() => {
        setIsRefetching(false);
      });
  }, [imageId, isRefetching, relay]);

  if (!image) {
    return null;
  }

  const sources =
    image?.sizes?.map(
      (size): ImageSource => ({
        uri: size.url,
        width: size.width / PixelRatio.get(),
        height: size.height / PixelRatio.get(),
        cacheKey: size.id,
      })
    ) ?? [];

  if (!imageId) {
    return null;
  }

  return (
    <I
      {...props}
      recyclingKey={imageId}
      responsivePolicy="initial"
      onError={() => {
        refetchQuery();
      }}
      transition={{
        duration: 300,
        effect: "cross-dissolve",
      }}
      source={
        sources.length > 0
          ? sources
          : uploadedImages
          ? [
              {
                uri: uploadedImages[image.id]?.localImageURI,
              },
            ]
          : []
      }
      placeholder={
        image?.thumbhash
          ? {
              thumbhash: image?.thumbhash,
            }
          : undefined
      }
    />
  );
}
